// ADR-043 / cycle-4 WI-19+WI-20+WI-21: FE i18n resolvers for BE-emitted
// codes (error_code, message_code). BE returns stable enum-like codes +
// structured args; FE resolves localized prose against the active UI locale.
//
// Two surfaces:
//   resolveApiError(payload) — for HTTP 4xx/5xx error responses.
//   resolveMessageCode(payload) — for SSE progress events + chat-message
//     metadata payloads.
//
// Both look up in the `errors:` and `progress:` / `chat:` namespaces per the
// `code` semantics. Unknown codes fall through to the defensive English
// `message`/`content` field (or empty string) and log a warning per
// `core-principles.md` "fail loud, log structured".

import i18n from '../i18n';
import { DATA_PANEL_REASON_CODES } from '../utils/dataPanelGates';

/** Shape of any 4xx/5xx body that may carry an ADR-043 error code. */
export interface ApiErrorPayload {
  error?: string;
  error_args?: Record<string, unknown>;
  // Legacy defensive English fallback. Will be absent on newly-migrated
  // endpoints (WI-11, WI-21). Keep optional so resolver works on both old
  // and new shapes during rollout — defensive fallback is the ADR-043
  // intent for non-FE callers, never relied on by FE when the code is
  // recognized.
  message?: string;
  detail?: string;
}

export function resolveApiError(payload: ApiErrorPayload | null | undefined): string {
  if (!payload) return '';
  if (payload.error) {
    const { key, args } = sentenceFor(payload);
    if (i18n.exists(key)) {
      return i18n.t(key, args) as string;
    }
    // Unknown code → fail loud + fall through to defensive English (ADR-043).
     
    console.warn(`[resolveApiError] Unrecognized API error code: ${payload.error}`);
  }
  return payload.message ?? payload.detail ?? '';
}

/**
 * ➕ WI-61 — the interpolation values for an ADR-043 error sentence.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * 🔴 WHY THIS EXISTS AT ALL: ONE `error_args` MEMBER IS ITSELF A CODE, AND A CODE MUST NEVER REACH
 * THE DOM.
 *
 * `workspace_write_forbidden` (403, `WorkspaceAccessRefusal.Forbidden`) carries its REASON as an
 * argument — `error_args.reason`, drawn from `ModelAccessResolver`'s closed vocabulary. That shape
 * is deliberate and the API says why at the site: two of the reasons already exist as top-level
 * ADR-043 codes whose shipped copy was written for a DIFFERENT surface, and one key cannot be true
 * in both places.
 *
 * ⚠️ THIS SAID "is ONE wire code" UNTIL 2026-09-03 AND THERE ARE NOW TWO — see
 * `REASON_CODE_BEARING_ERRORS`. The operator ruling closing `TD-942` made workspace CREATION refuse
 * a caller with no `model_access` row, and `workspace_create_forbidden` carries the same vocabulary
 * on the same slot. Corrected here rather than left to be inferred from the constant, because the
 * word "ONE" is exactly the kind of count a later reader takes as a licence to hardcode.
 *
 * Everywhere else in this function an `error_args` member is a VALUE — a filename, a count, a status
 * — and passing it straight through is right. Here it is an internal identifier, and TD-321 forbids
 * those in user copy by name. So it is resolved to PROSE first, through
 * `resolveModelWriteDenied` — the ONE function that owns that vocabulary and is documented as
 * *"always returns non-empty localized prose"* — and the sentence interpolates the prose.
 *
 * ⚠️ WHAT WAS ACTUALLY BROKEN, because the shape of the bug is not the shape of the fix. The API has
 * been producing `error_args.reason` correctly all along and `resolveModelWriteDenied` has had copy
 * for every code in all three locales. The reason was dropped because `errors:workspace_write_forbidden`
 * carried **no `{{reason}}` placeholder**, so i18next interpolated nothing and the user saw the
 * generic *"You don't have permission to change this project."* — for BOTH axes of a two-axis
 * refusal, including the one an administrator has to fix and the model's owner cannot. That is a
 * whitelist-drop, not a missing capability: ✅ positive control — 12 FE files read `error_args`, and
 * `WorkspacePage.tsx` unwraps `error_args.sections`, so presence was possible and detectable.
 *
 * ⛔ IT IS FIXED HERE AND NOT PER SCREEN. Every consumer of this refusal reaches it through
 * `resolveApiError` (the workspace plane arrives via `HttpBennieClient.localizeErrorBody`), so a
 * per-screen unwrap would fix one caller and leave the rest reading the generic sentence — the same
 * class of partial fix that let the drop survive on the workspace plane while the model plane
 * rendered it correctly.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 */
function sentenceFor(payload: ApiErrorPayload): { key: string; args: Record<string, unknown> } {
  const args = payload.error_args ?? {};
  const generic = { key: `errors:${payload.error}`, args };

  // ─────────────────────────────────────────────────────────────────────────────────────────────
  // A TERMINAL WITH NO SECTION IN SCOPE GETS ITS OWN SENTENCE, NOT THE SAME ONE WITH A STAND-IN.
  //
  // Some export guards scan the WHOLE render payload rather than one section, so the API omits
  // `error_args.section` entirely — there is no section to name. The copy for the scoped case ends
  // "Regenerate that section", which under a whole-document subject instructs the user to
  // regenerate a section the same sentence says does not exist.
  //
  // ⛔ The backend deliberately does NOT send a substitute noun phrase. It cannot: the only locale
  // it holds is the WORKSPACE OUTPUT language (what the report is written in), while this sentence
  // renders in the READER'S UI language. A section title is fine on that axis because it is data;
  // UI prose is not. So the language-specific half of the fix lives here, where the reader's
  // language is actually known.
  //
  // This is the SAME SHAPE as the `_because` pair below and for the same stated reason: a different
  // KEY, never the same sentence with an empty slot, because i18next renders an unsupplied
  // `{{section}}` verbatim on screen. Guarded by `i18n.exists`, so a code without a `_document`
  // variant is untouched.
  // ─────────────────────────────────────────────────────────────────────────────────────────────
  // Auditing rework rev 9 (PR 9, owed by PR 5): `audit_phase_checklist_not_approved` names its
  // phase as the WIRE TAG (`error_args.audit_phase` = `de` / `oe`, or the long wire value). A tag
  // is a code, so it is turned into the localized phase name here; a body without a recognisable
  // phase uses the unnamed sentence rather than leaving `{{phase}}` on screen.
  if (payload.error === 'audit_phase_checklist_not_approved') {
    const phaseKey = AUDIT_PHASE_LABEL_KEYS[String(args.audit_phase ?? '')];
    return phaseKey
      ? { key: 'errors:audit_phase_checklist_not_approved', args: { ...args, phase: i18n.t(phaseKey) } }
      : { key: 'errors:audit_phase_checklist_not_approved_unnamed', args };
  }

  const section = args.section;
  if (typeof section !== 'string' || section.length === 0) {
    const documentKey = `errors:${payload.error}_document`;
    if (payload.error && i18n.exists(documentKey)) return { key: documentKey, args };
  }

  if (!REASON_CODE_BEARING_ERRORS.includes(payload.error ?? '')) return generic;

  const reason = args.reason;
  // ⛔ A body with NO reason keeps the GENERIC key. It is a different key rather than the same
  // sentence with an empty slot, because i18next leaves an unsupplied `{{reason}}` in the output
  // verbatim — a placeholder on screen — and an empty-string substitution would leave a dangling
  // sentence. "We could not say why" and "the reason is blank" must not render the same.
  if (typeof reason !== 'string' || reason.length === 0) return generic;

  // ➕ 🔴 `N-6` (2026-09-06) — THE SECOND AXIS RIDES BESIDE THE REASON, AND IT IS RESOLVED HERE
  // BECAUSE THIS IS THE ONE PLACE THAT TURNS A CODE INTO PROSE.
  //
  // ⚠️ `reason_context` IS NOT AN API-EMITTED MEMBER, AND SAYING SO IS THE POINT. Every 4xx in this
  // module is produced by ONE gate, so a refusal-in-flight has exactly one reason and the API has
  // nothing to put here. The AFFORDANCE is different in kind: it is a POSTURE, not a refusal, and
  // `GET /api/models/{modelId}` publishes both axes of it side by side per service type
  // (`create_denied_reason` and `write_denied_reason` on the same entry). `resolveWorkspaceCreateDenied`
  // is what spends the second one — see its own note, and `resolveModelWriteDenied`'s `context`
  // parameter for the full defect record.
  //
  // ⛔ It is threaded rather than re-spelled at the call site: `sentenceFor` owns the
  // `_because`-vs-generic key choice AND the code→prose resolution, and a second composition beside
  // it is the divergence this function exists to prevent. Absent / non-string ⇒ nothing changes.
  const reasonContext = args.reason_context;
  return {
    key: `errors:${payload.error}_because`,
    args: {
      ...args,
      reason: resolveModelWriteDenied(
        reason, typeof reasonContext === 'string' ? reasonContext : undefined),
    },
  };
}

/**
 * Every wire code whose `error_args.reason` is itself a CODE rather than a value. See `sentenceFor`.
 *
 * ⚠️ THIS WAS A SINGLE CONSTANT UNTIL 2026-09-03 (`const WORKSPACE_WRITE_FORBIDDEN`), documented as
 * *"The ONE wire code…"*. The operator ruling closing `TD-942` added a second: workspace CREATION now
 * refuses a caller with no `model_access` row, and its refusal carries the SAME closed vocabulary on
 * the same `error_args.reason` slot.
 *
 * 🔴 THE TWO CODES ARE SEPARATE ON PURPOSE AND MUST NOT BE COLLAPSED. `workspace_write_forbidden`'s
 * copy ends *"You can still open it and read it"*, which is a sentence about a workspace that
 * EXISTS. At creation there is nothing to open. One key cannot be true in both places — the same
 * collision the API records at `WorkspaceAccessRefusal` and at the create handler's per-model leg.
 * What they SHARE is the reason vocabulary, and that is resolved once, by
 * `resolveModelWriteDenied`, for both.
 *
 * Each member needs a matching `errors:<code>_because` key carrying a `{{reason}}` slot; a member
 * without one silently loses the reason, which is the exact defect WI-61 fixed on the first code.
 * `apps/test-scripts/frontend/i18n/twoAxisRefusalSentence.test.ts` asserts that pairing per member.
 *
 * ➕ 2026-09-08 — A THIRD, from operator ruling `R-C3` (the Toolbox is model-scoped).
 * `toolbox_model_access_forbidden` is emitted by the five create routes AND by every per-job route
 * (`ToolboxEndpoints.ToolboxModelAccessForbidden`) and carries the SAME `ModelAccessResolver`
 * vocabulary on the same `error_args.reason` slot.
 *
 * 🔴 IT IS A THIRD CODE RATHER THAN A REUSE OF EITHER EXISTING ONE, for the reason already recorded
 * above: both of those are written for a WORKSPACE and their copy says so ("start a project under
 * this model", "you can still open it and read it"). A Toolbox run is not a project and there is
 * nothing to open. What the three SHARE is the reason vocabulary, and that is still resolved once,
 * by `resolveModelWriteDenied`, for all of them — no fourth copy of those sentences.
 *
 * ⛔ `toolbox_job_not_model_scoped` (the other new `R-C3` refusal) is deliberately NOT a member. Its
 * `error_args` carries `job_id`, a VALUE, not a reason code — it names a job that records no model
 * at all, which is a could-not-measure rather than a membership verdict, and `sentenceFor` must
 * pass it straight through to the generic key.
 */
/** The auditing phase wire values (short tag and long form) → the tab-label key that names them. */
const AUDIT_PHASE_LABEL_KEYS: Record<string, string> = {
  de: 'auditing:tabs.designEffective',
  design_effective: 'auditing:tabs.designEffective',
  oe: 'auditing:tabs.operatingEffective',
  operating_effective: 'auditing:tabs.operatingEffective',
};

export const REASON_CODE_BEARING_ERRORS: readonly string[] = [
  'workspace_write_forbidden',
  'workspace_create_forbidden',
  'toolbox_model_access_forbidden',
];

/**
 * ➕ WI-91 (2026-09-04) — the create-denial sentence, off the AFFORDANCE rather than off the 403.
 *
 * 🔴 **THIS AUTHORS NO COPY, AND THAT IS THE WHOLE POINT.** `errors:workspace_create_forbidden` and
 * its `_because` variant already ship in all three locales and already compose through
 * `sentenceFor` → `resolveModelWriteDenied`. What did NOT exist was a way to reach them WITHOUT an
 * API error: the sentence was reachable only after the user pressed a control and got a 403 back.
 * A greyed "+ New project" never issues a request, so the person who most needs the sentence was
 * the one person who could not see it. That is a CARRIAGE gap; minting a fourth copy of an
 * already-shipped sentence would have been the wrong fix.
 *
 * ⛔ It goes through `resolveApiError`, not around it. `sentenceFor` owns the `_because`-vs-generic
 * key choice AND the code→prose resolution of `error_args.reason`; re-spelling either here would be
 * a second implementation of the composition this module exists to hold once. The payload handed in
 * is the same shape the API emits for this exact refusal, carrying the same closed-vocabulary code
 * — nothing is invented to satisfy the contract.
 *
 * A null / absent `reasonCode` yields the GENERIC create-denial sentence rather than one with an
 * empty slot, which is `sentenceFor`'s own documented behaviour: "we could not say why" and "the
 * reason is blank" must not render the same.
 *
 * ➕ 🔴 **`N-6` (2026-09-06) — `perModelReason` IS THE OTHER AXIS, AND THE CLAIM ABOVE THAT THIS
 * PAYLOAD IS "the same shape the API emits" IS NARROWED RATHER THAN LEFT TO BE DISCOVERED.** It is
 * still true of `reason`. It is NOT true of `reason_context`: the API emits one reason because a 403
 * is produced by one gate, while this call site is describing a POSTURE whose two axes the model
 * payload publishes side by side (`service_types[type].create_denied_reason` and
 * `…write_denied_reason`). Widening the args rather than faking a second 403 is the honest form —
 * `core-principles.md`: adapt the contract, never invent the input.
 *
 * @param perModelReason the SAME entry's `write_denied_reason` — the per-model leg's own verdict for
 *   the subject the create door would evaluate. Pass it whenever the caller holds it; it is dropped
 *   when it equals `reasonCode` (the two axes agreed and there is nothing extra to say) and ignored
 *   when no paired copy exists. See `resolveModelWriteDenied`'s `context` for why one code cannot
 *   carry both axes and why the SERVER's precedence is deliberately not the fix.
 */
export function resolveWorkspaceCreateDenied(
  reasonCode?: string | null, perModelReason?: string | null): string {
  return resolveApiError({
    error: 'workspace_create_forbidden',
    error_args: reasonCode
      ? { reason: reasonCode, ...(perModelReason ? { reason_context: perModelReason } : {}) }
      : {},
  });
}

/** Shape of any SSE / chat-message payload that may carry a message_code. */
export interface MessageCodePayload {
  message_code?: string;
  message_args?: Record<string, unknown>;
  // Defensive fallback only — FE never reads this when message_code is
  // recognized. Will be null/absent for newly-migrated SSE/chat surfaces.
  message?: string | null;
  content?: string | null;
}

/**
 * Resolve a message_code-bearing payload to localized prose.
 *
 * @param payload — the SSE event / chat metadata.
 * @param namespace — the i18n namespace (default 'progress' for SSE; pass
 *   'chat' for chat-panel messages, 'errors' for error-equivalent codes).
 * @param fallback — default text if neither code nor `message`/`content` resolves.
 */
export function resolveMessageCode(
  payload: MessageCodePayload | null | undefined,
  namespace: 'progress' | 'chat' | 'errors' = 'progress',
  fallback = '',
): string {
  if (!payload) return fallback;
  if (payload.message_code) {
    const key = `${namespace}:${payload.message_code}`;
    if (i18n.exists(key)) {
      return i18n.t(key, payload.message_args ?? {}) as string;
    }
     
    console.warn(`[resolveMessageCode] Unrecognized ${namespace} code: ${payload.message_code}`);
  }
  return payload.message ?? payload.content ?? fallback;
}

// ---------------------------------------------------------------------------
// TD-206: section FailureDiagnostic error_code → user-facing failure copy.
//
// The BE persists FailureDiagnostic (error_code / error_stage / error_message)
// on failed sections for agent, ops, and log consumption (SectionModels.cs).
// Those values are internal pipeline vocabulary (e.g. the QA-2026-07-07 repro
// rendered `PIPELINE_EXIT_STUCK_SECTION · supervisor_sweep — …non-terminal
// status 'context_ready'…` verbatim in the Plan Tracker) and must NEVER reach
// the DOM — not even in a tooltip (frontend-rules.md "Error Display"). The FE
// maps each known code to one of a small set of failure CLASSES and renders
// the class copy from `errors:sectionFailure.<class>`. Unknown codes fail
// loud (console.warn) and render the generic `default` copy — the failure
// stays visibly failed; only the vocabulary is user-appropriate. Diagnostics
// remain intact in the network payload and logs.

export type SectionFailureClass =
  | 'transient'       // temporary provider/service issue — wait and retry
  | 'context'         // source context could not be assembled — retry / add sources
  | 'outOfEvidence'   // the draft cited a source outside this report's evidence — regenerate
  | 'reviewNotPassed' // a draft was produced but failed automated review — regenerate
  | 'reviewUnavailable' // the automated review could not produce a result — retry runs it again
  | 'truncated'       // the response was cut off before it finished — retry
  | 'execSummary'     // executive-summary pipeline failure — retry after body completes
  | 'incomplete'      // generation did not complete — retry
  | 'cancelled'       // the user stopped generation — retry to resume
  | 'interrupted';    // stopped by something other than the user (e.g. a service restart) — retry

/** Every FailureDiagnostic ErrorCode the BE emits, classed for user copy. */
export const SECTION_FAILURE_CLASS_BY_CODE: Record<string, SectionFailureClass> = {
  ANTHROPIC_CLIENT_TIMEOUT: 'transient',
  AWAITING_INFRASTRUCTURE_RETRY: 'transient',
  CONTENT_GEN_TIMEOUT: 'transient',
  CONTEXT_PREP_TIMEOUT: 'transient',
  CTX_ENG_TIMEOUT: 'transient',
  HOLISTIC_REGEN_TIMEOUT: 'transient',
  LLM_INFRASTRUCTURE_ERROR: 'transient',
  REGEN_PLAN_FETCH_TIMEOUT: 'transient',
  REGEN_SSE_PIPE_CANCELLED: 'transient',
  CCW2_FACTS_MISSING: 'context',
  CCW2_FACTS_READ_FAILED: 'context',
  AGENT_STALL: 'context',
  CONTEXT_PREP_FAILED: 'context',
  CTX_ENG_MAX_ROUNDS: 'context',
  RETRIEVAL_INCOMPLETE: 'context',
  ECHARTS_BLOCK_INVALID: 'reviewNotPassed',
  CITATION_NO_REFS: 'reviewNotPassed',
  CITATION_OUT_OF_BOUNDS: 'reviewNotPassed',
  CITATION_OUT_OF_EVIDENCE: 'outOfEvidence',
  HOLISTIC_REGEN_EXHAUSTED: 'reviewNotPassed',
  HOLISTIC_REGEN_NO_VALID_CONTENT: 'reviewNotPassed',
  HOLISTIC_VALIDATION_UNPARSEABLE: 'reviewUnavailable',
  HOLISTIC_VALIDATION_THREW: 'reviewUnavailable',
  PHASE2D_ISSUE_MISMATCH: 'reviewNotPassed',
  SECTION_VERSION_EXHAUSTED: 'reviewNotPassed',
  TABLE_SCHEMA_MISMATCH: 'reviewNotPassed',
  TRUNCATION_AT_MAX_TOKENS: 'truncated',
  VERBATIM_VERSION_EXHAUSTED: 'reviewNotPassed',
  EXEC_SUMMARY_REGEN_RESET: 'execSummary',
  PHASE2C_EXEC_SUMMARY_EMPTY_CONTENT: 'execSummary',
  PHASE2C_EXEC_SUMMARY_LLM_FAILED: 'execSummary',
  PHASE2C_EXEC_SUMMARY_PARSE_FAILED: 'execSummary',
  PHASE2C_EXEC_SUMMARY_PERSIST_FAILED: 'execSummary',
  PHASE2C_EXEC_SUMMARY_SECTION_MISSING: 'execSummary',
  PHASE2C_EXEC_SUMMARY_SHAPE_FAILED: 'execSummary',
  PHASE2C_EXEC_SUMMARY_TRUNCATED: 'execSummary',
  PHASE2C_PROMPT_SET_MISSING: 'execSummary',
  COHORT_RELEASE_AUTO_DISPATCH_FAILED: 'incomplete',
  CONTENT_GEN_CRASHED: 'incomplete',
  CONTENT_GEN_FAILED: 'incomplete',
  CONTENT_GEN_ZERO_TOKENS: 'incomplete',
  HOLISTIC_REVAL_DISPATCH_SETUP_FAILED: 'incomplete',
  NO_CONTENT_GENERATED: 'incomplete',
  PENDING_VALIDATION_ORPHAN: 'incomplete',
  PIPELINE_EXIT_STUCK_SECTION: 'incomplete',
  PIPELINE_ORPHANED_AT_STARTUP: 'incomplete',
  REGEN_PERSIST_FAILED: 'incomplete',
  REGEN_PLAN_FETCH_FAILED: 'incomplete',
  REGEN_SSE_HANDLER_EXCEPTION: 'incomplete',
  SECTION_GEN_EMPTY_RESPONSE: 'incomplete',
  SECTION_GEN_EXCEPTION: 'incomplete',
  regen_status_write_stuck: 'incomplete',
  // A user Stop Generation vs any other cancel (host shutdown, time budget) — the BE picks the code
  // by cause (ContentGenStreamPipeline.CancelDiagnostic).
  CANCELLED_BEFORE_CONTEXT_PREP: 'cancelled',
  CTX_ENG_ABORTED: 'cancelled',
  SECTION_GEN_CANCELLED: 'cancelled',
  CTX_ENG_INTERRUPTED: 'interrupted',
  INTERRUPTED_BEFORE_CONTEXT_PREP: 'interrupted',
  SECTION_GEN_INTERRUPTED: 'interrupted',
};

/**
 * True when a persisted section error_code is a user Stop Generation (ContentGenStreamPipeline.
 * CancelDiagnostic). Host shutdown / budget interrupts are a different class and stay failures.
 * The status half of the question lives in `isUserStoppedSection` (types/reportSections.ts).
 */
export function isUserStopCode(errorCode?: string | null): boolean {
  return !!errorCode && SECTION_FAILURE_CLASS_BY_CODE[errorCode] === 'cancelled';
}

/**
 * Resolve a section failure to user-facing copy. Always returns non-empty
 * localized prose (never the raw slug) so the failed state stays visible.
 */
export function sectionFailureKey(errorCode?: string | null): string {
  const cls = errorCode ? SECTION_FAILURE_CLASS_BY_CODE[errorCode] : undefined;
  if (errorCode && !cls) {
    console.warn(`[resolveSectionFailure] Unrecognized section failure code: ${errorCode}`);
  }
  return `errors:sectionFailure.${cls ?? 'default'}`;
}

export function resolveSectionFailure(errorCode?: string | null): string {
  return i18n.t(sectionFailureKey(errorCode)) as string;
}

// ---------------------------------------------------------------------------
// Export-target ineligibility: `reason_code` → why this workspace can't take the export.
//
// GET /api/workspaces/export-targets reports the SAME gate the import endpoint enforces
// (WorkspaceImportGate). It used to ship the verdict twice — a machine-readable
// `reason_code` AND server-rendered English `reason` prose — and the export modal
// rendered NEITHER: an ineligible workspace appeared greyed out and unclickable with no
// explanation, which is the "no meaningful message" half of frontend-rules "Error
// Display". The prose field is now gone (it could never follow the UI locale), so the
// code is the only fact and localizing it is the client's job.
//
// Same shape as resolveSectionFailure above, and for the same reason: the codes are
// internal state vocabulary and must never reach the DOM. The allowlist is exactly what
// WorkspaceImportGate.Reasons emits — a code added server-side without copy here fails
// loud (console.warn) and renders the generic line rather than blank or the raw slug.

/** Every ineligibility code the gate emits, in `WorkspaceImportGate.Reasons` order. */
export const EXPORT_INELIGIBILITY_REASON_CODES = [
  'archived',
  // B-1 / ruling R-C (2026-08-23): locked BY INHERITANCE — the workspace's own status is untouched,
  // typically still `active`, so this is NOT `archived` and must not share its copy.
  'model_archived',
  'no_workflow_state',
  'no_approved_plan',
  'report_generation_started',
  'failed_state',
  'workspace_busy',
  // ➕ B16 item 2 (2026-09-10): the operator ruling collapsed the export condition onto the
  // report-generation trigger, so `GET /api/workspaces/export-targets` classifies with
  // `WorkspaceParkGate` — whose three-valued cutoff emits this for a persisted state the build
  // cannot place. It was already a `WorkspaceImportGate.Reasons` member; only its REACHABILITY on
  // this surface is new, and an unlisted code renders the generic line plus a console.warn.
  'unknown_workflow_state',
  // Auditing rework PR 5 (review B3, 2026-09-27): export-targets reports an auditing workspace
  // ineligible because its import must name an evidence phase the export modal cannot supply.
  'auditing_import_requires_phase',
  // 2026-10-23 (operator consent): export is admitted at the settled report state, so the user's
  // `complete` lock is now reported as its own code rather than `report_generation_started`.
  'workspace_terminal',
] as const;

/**
 * Resolve an export target's `reason_code` to user-facing copy. Always returns non-empty
 * localized prose — never blank, never the code itself.
 *
 * Call only for an INELIGIBLE target: an eligible one carries no reason, and asking for
 * one is a caller bug the warn will surface.
 */
export function resolveExportIneligibility(reasonCode?: string | null): string {
  const known = (EXPORT_INELIGIBILITY_REASON_CODES as readonly string[])
    .includes(reasonCode ?? '');
  if (!known) {
    console.warn(`[resolveExportIneligibility] Unrecognized export ineligibility code: ${reasonCode}`);
  }
  return i18n.t(`toolbox:exportModal.ineligibility.${known ? reasonCode : 'default'}`) as string;
}

// ---------------------------------------------------------------------------
// WI-10a: model-registry `caller.write_denied_reason` → why this caller can't edit this model.
//
// GET /api/models/{modelId} publishes a per-caller `caller` block whose `write_denied_reason` is
// the write gate's own refusal code. Locked answer Q2 of `fe-design-lock-2026-08-22.md` requires
// that a caller who may not write sees the control **rendered and disabled WITH A STATED REASON,
// never hidden** — "hiding says the capability does not exist; greying says it exists and is not
// yours." An empty string is not a stated reason, so this resolver exists to make blank
// unreachable.
//
// ⚠️ IT IS NOT `resolveApiError`, AND THAT IS THE WHOLE POINT. The reason rides a **200** body, so
// there is no `message` / `detail` beside it to fall through to — `resolveApiError` would return
// `''` for every value in the vocabulary. Nor can the codes simply be catalogued at the top level
// of `errors:`: `model_archived` is ALSO an ADR-043 error code, and its top-level copy is the
// workspace-CREATE sentence ("…so new workspaces can't be created under it"), which is false on an
// edit chip. Scoping the namespace keeps both surfaces truthful with one string each.
//
// ⚠️ TD-845's CI gate CANNOT SEE THESE CODES. `infra/assert-error-codes-localized.py` derives its
// universe from `error = "<code>"` emissions in C#; a 200-body field is not one, so coverage here
// is held by `modelWriteDeniedCopy.test.ts` against the resolver's own vocabulary, not by that gate.
//
// Same shape as resolveSectionFailure / resolveExportIneligibility above, and for the same reason:
// a code added server-side without copy here fails loud (console.warn) and renders the generic
// line rather than blank or the raw slug.
//
// ⚠️ WI-10b WIDENED THE VOCABULARY AND GAVE IT A SECOND CARRIER, so "the model payload" is no
// longer the whole story. Four of the eight codes below CANNOT appear on a 200 model payload:
//   * `service_type_not_granted` / `service_type_not_entitled` — the two legs ruling `R-D` ANDs.
//     They are reachable only when the write has a SUBJECT (work beneath the model). The model
//     payload resolves with no subject, so a collaborator reading a MODEL is always
//     `role_collaborator` and never one of these two.
//   * `workspace_not_model_rooted` / `workspace_model_unresolvable` — `ModelAccessResolver`
//     declares them and never emits them (its own heading: "Two codes this type OWNS but does not
//     EMIT"). They describe the failure to obtain a model row at all, on the workspace→model edge
//     that only `WorkspaceMutabilityGuard` walks.
// All four reach the client the same way: as `error_args.reason` on the **403**
// `workspace_write_forbidden` body (`Endpoints/WorkspaceAccessRefusal.cs`), which deliberately
// feeds THIS namespace rather than minting a second one — "the identical collision the model plane
// solved by giving the chip its own namespace". Their copy is therefore written for the WORKSPACE
// surface, because that is the only surface that can render them.
//
// ⚠️ AND `role_collaborator` CHANGED MEANING WITHOUT CHANGING SPELLING (§18 #7), which is the one
// case where re-reading shipped copy is not optional. It used to mean "your tier authorises
// nothing"; it now means "a collaborator's grant does not reach the MODEL RECORD". The locked mock,
// verbatim: "A collaborator can change work of the service types ticked for them. Everything else
// on this model stays readable to them, and to everyone." The three bundles were rewritten in the
// same change that added the four codes — the old sentence ("Collaborator access is currently
// view-only") is now false, and a false sentence renders exactly as confidently as a true one.

/** Every refusal code `ModelAccessResolver` OWNS, in its declaration order. */
export const MODEL_WRITE_DENIED_REASON_CODES = [
  'model_archived',
  'principal_oid_unresolved',
  'no_access_row',
  // `RoleReasonPrefix + role` for every role 0045's CHECK admits EXCEPT `owner`, which the write
  // gate short-circuits to a grant before it can reach the role-naming line.
  'role_collaborator',
  // WI-10b — subject-bearing writes only (the workspace plane). See the note above.
  'service_type_not_granted',
  'service_type_not_entitled',
  // WI-10b — owned here, emitted by `WorkspaceMutabilityGuard`. See the note above.
  'workspace_not_model_rooted',
  'workspace_model_unresolvable',
] as const;

/**
 * Resolve a model-access refusal code to user-facing copy. Always returns non-empty localized
 * prose — never blank, never the code itself.
 *
 * TWO CARRIERS, one vocabulary. Call it either with `caller.write_denied_reason` off a 200 model
 * payload (`caller.may_write` false — a caller who MAY write carries `null` there, and asking for a
 * reason is a caller bug the warn will surface), or with `error_args.reason` off a 403
 * `workspace_write_forbidden` body. The top-level `errors:workspace_write_forbidden` key carries the
 * generic sentence for that 403; this resolver is what turns its `reason` into the specific one.
 *
 * @param context ➕ 🔴 **`N-6` (2026-09-06) — THE SECOND AXIS, WHEN THE CODE ALONE CANNOT SAY WHICH
 *   ONES FAILED.** Selects `errors:modelWriteDenied.<code>_<context>` where one exists and the base
 *   line otherwise — i18next's own contract, and the identical mechanism
 *   `resolveDataPanelIneligibility` spends `F-BUSY-01`'s refusal slug through.
 *
 *   <p>🔴 <b>WHY IT IS NEEDED AT ALL: ONE CODE MEANS TWO DIFFERENT THINGS ON TWO DOORS.</b>
 *   `ModelAccessResolver.ReasonServiceTypeNotEntitled` DECLARES a conjunction — *"the caller's
 *   per-model grant DOES cover this service type, but their deployment-wide entitlement does not"* —
 *   and `WriteDeniedReason` enforces that precondition structurally, so on the WRITE door the code
 *   really does carry a claim about the per-model axis. On the CREATE door it does not:
 *   `CallerServiceTypeAccessWire.From` hoists it over ANY per-model refusal, because
 *   `POST /api/workspaces` genuinely refuses on the entitlement axis FIRST (ruling `F-1`,
 *   2026-09-05). Measured live on DEV: a caller with `write_denied_reason: "no_access_row"` was
 *   published `create_denied_reason: "service_type_not_entitled"` and read *"Your access on this
 *   model already covers this type of work"* — an assertion about an axis nothing had checked. That
 *   sends the person to an administrator for a deployment entitlement when they ALSO need access on
 *   the model, and a refusal that misdirects is worse than a blunt one.</p>
 *
 *   <p>⛔ <b>THE SERVER IS NOT THE DEFECT AND MUST NOT MOVE.</b> Reversing the hoist re-opens `F-1`'s
 *   own stated harm (*"would send them to the model's owner when only an administrator can help"*)
 *   and puts the published reason back into disagreement with the door it mirrors. The collision is
 *   the one this module already records twice — `model_archived`'s create sentence being false on an
 *   edit chip, and *"the same CODE means the same fact on both surfaces but needs a different
 *   SENTENCE"*. Same cure, third time: a different KEY, never a different gate.</p>
 *
 *   <p>⚠️ <b>THE DISCRIMINATOR IS ALREADY ON THE WIRE AND IS NOT PUBLISHED TWICE.</b> It is
 *   `caller.service_types[type].write_denied_reason` — the per-model leg's own verdict for the SAME
 *   subject, i.e. exactly the value the create reason hoists over. Adding a `create_denied_context`
 *   field beside it would give one fact two places to disagree, which is the reason
 *   `CallerServiceTypeAccessWire` carries no `role`.</p>
 *
 *   <p>⚠️ <b>A CONTEXT WITH NO KEY FALLS BACK TO THE BASE SENTENCE, AND THAT IS SAFE ONLY BECAUSE
 *   THE BASE SENTENCE IS NOW TRUE ON BOTH DOORS.</b> Its over-claiming first clause was retired in
 *   the same change; what a fallback now costs is completeness, not accuracy. Coverage of the pairs
 *   that DO need their own sentence is asserted per locale by
 *   `apps/test-scripts/frontend/i18n/bothAxisRefusalSentence.test.tsx`, because i18next's fallback
 *   fails soft and a missing pair would look exactly like the state this fix removes.</p>
 */
export function resolveModelWriteDenied(
  reasonCode?: string | null, context?: string | null): string {
  const known = (MODEL_WRITE_DENIED_REASON_CODES as readonly string[])
    .includes(reasonCode ?? '');
  if (!known) {
    console.warn(`[resolveModelWriteDenied] Unrecognized model write-denial code: ${reasonCode}`);
  }
  // A slug EQUAL to the code carries nothing (the two axes agreed), and an empty one is not a slug.
  // Same two drops `resolveDataPanelIneligibility` makes, for the same reason.
  const slug = context && context !== reasonCode ? context : undefined;
  return i18n.t(
    `errors:modelWriteDenied.${known ? reasonCode : 'default'}`,
    slug ? { context: slug } : {}) as string;
}

// ---------------------------------------------------------------------------
// WI-26: the data panel's `capabilities.source_data_upload.reason_code` → why this project can't
// take source data right now.
//
// GET /api/workspaces/{id} projects the verdict the three POST /files/source-data arms ENFORCE, so
// the panel offers exactly what the upload will accept and refuses with exactly the reason it will
// refuse for. The codes are `WorkspaceImportGate.Reasons` — internal vocabulary that must never
// reach the DOM.
//
// ⚠️ ITS OWN NAMESPACE, NOT `toolbox:exportModal.ineligibility` AND NOT top-level `errors:` — and
// the reason is the one `resolveModelWriteDenied` already records one resolver up. The same CODE
// means the same fact on both surfaces but needs a different SENTENCE: the export copy is written
// for a dropdown row ("…then export into it"), and rendering it beside an upload area would tell
// the user to do something they are not doing. `model_archived` is additionally a top-level
// ADR-043 error code whose copy is the workspace-CREATE sentence, which is false here twice over.
// One string per surface keeps all three truthful.
//
// ⚠️ SEVEN CODES since 2026-10-21 (`workspace_terminal`, the user's `complete` lock — see
// `DATA_PANEL_REASON_CODES`); it read "SIX CODES, NOT SEVEN". The vocabulary is `DATA_PANEL_REASON_CODES` (utils/dataPanelGates),
// which omits the two `Reasons` members this surface cannot emit. Catalogue a seventh here and the
// panel claims to understand a refusal it can never be handed.

// 🔴 F-BUSY-01 (2026-09-06) — THE CODE ALONE IS NOT A SENTENCE, and shipping it as one put a FALSE
// line in front of every brand-new project. `WorkspaceImportGate.Reasons` is deliberately coarse:
// `workspace_busy` is the fall-through for every non-error state below the report-generation cutoff,
// and on the upload surface that population is exactly `project_created` / `draft` /
// `files_uploaded` / `plan_generating` / `plan_ready` — a project on which nothing is running and
// nothing will finish. "This project is busy — wait for the current step to finish" is false in both
// halves, and the-truth.md §3.2 requires this panel to say "Upload source files after plan is
// approved" instead.
//
// The discriminator already existed and was already shipped: the enforcing 409 carries
// `error_args.context` BESIDE the code (`SourceDataUploadGate.RefusalContext`), which is how
// `errors:upload_not_allowed_before_plan` gets selected. WI-26 projected the code without the slug,
// so the panel had nothing finer than the bucket. The server now projects both; this resolver spends
// the second one.
//
// ⚠️ THE PAIR KEY FAILS SOFT, WHICH IS WHY IT NEEDS A GATE. i18next resolves a missing
// `<code>_<slug>` by silently falling back to `<code>` — the generic line — so a slug landing
// without copy looks exactly like the state this fix removes. `dataPanelIneligibilityCopy.test.ts`
// asserts the pair copy in all three locales, the same way `refusalContextCopy.test.ts` does for the
// 409's half.

/**
 * Resolve a data-panel `reason_code` (+ its phase slug) to user-facing copy. Always returns
 * non-empty localized prose — never blank, never the code itself.
 *
 * Call only for an INELIGIBLE panel: an eligible one carries no reason, and asking for one is a
 * caller bug the warn will surface.
 *
 * @param context the server's `capabilities.sourceDataUpload.context`. Selects the
 *   `<code>_<context>` line where one exists and the base line otherwise — i18next's own contract,
 *   matching how the 409's `error_args.context` is spent. A slug EQUAL to the code is dropped: for
 *   the two lock reasons `RefusalContext` returns the code itself (a lock names itself), and
 *   `archived_archived` would be a key nothing catalogues.
 */
export function resolveDataPanelIneligibility(
  reasonCode?: string | null, context?: string | null): string {
  const known = (DATA_PANEL_REASON_CODES as readonly string[]).includes(reasonCode ?? '');
  if (!known) {
    console.warn(
      `[resolveDataPanelIneligibility] Unrecognized data-panel ineligibility code: ${reasonCode}`);
  }
  const slug = context && context !== reasonCode ? context : undefined;
  return i18n.t(
    `workspace:dataPanel.ineligibility.${known ? reasonCode : 'default'}`,
    slug ? { context: slug } : {}) as string;
}
